import type { TaskTypeModule } from './module';

export type TaskTypeRuntimeLoader = () => Promise<TaskTypeModule<any, any>>;

/**
 * Explicit import edges keep each task runtime in its own webpack chunk. Do
 * not replace this with a variable import: the bundler would lose the
 * selected-runtime boundary and may include every task view in one context
 * chunk.
 */
const RUNTIME_LOADERS: Record<string, TaskTypeRuntimeLoader> = {
  'arxiv-reasoning': () => import('./arxiv-reasoning').then(({ arxivReasoningModule }) => arxivReasoningModule),
  browsecomp: () => import('./browsecomp').then(({ browsecompModule }) => browsecompModule),
  bunsen: () => import('./bunsen').then(({ bunsenModule }) => bunsenModule),
  'clinical-rubrics': () => import('./clinical-rubrics').then(({ clinicalRubricsModule }) => clinicalRubricsModule),
  'comp-workflows-drug-discovery': () => import('./comp-workflows-drug-discovery').then(({ compWorkflowsDrugDiscoveryModule }) => compWorkflowsDrugDiscoveryModule),
  'comp-workflows-scientific': () => import('./comp-workflows-scientific').then(({ compWorkflowsScientificModule }) => compWorkflowsScientificModule),
  'computational-workflow': () => import('./computational-workflow').then(({ computationalWorkflowModule }) => computationalWorkflowModule),
  'computational-workflow-docker': () => import('./computational-workflow-docker').then(({ computationalWorkflowDockerModule }) => computationalWorkflowDockerModule),
  'document-rewrites': () => import('./document-rewrites').then(({ documentRewritesModule }) => documentRewritesModule),
  'environment-builder': () => import('./environment-builder').then(({ environmentBuilderModule }) => environmentBuilderModule),
  'feedback-form': () => import('./feedback-form').then(({ feedbackFormModule }) => feedbackFormModule),
  'finance-agent-conversations': () => import('./finance-agent-conversations').then(({ financeAgentConversationsModule }) => financeAgentConversationsModule),
  'finance-build-rubrics': () => import('./finance-build-rubrics').then(({ financeBuildRubricsModule }) => financeBuildRubricsModule),
  'finance-rubrics': () => import('./finance-rubrics').then(({ financeRubricsModule }) => financeRubricsModule),
  'gdm-rubrics': () => import('./gdm-rubrics').then(({ gdmRubricsModule }) => gdmRubricsModule),
  'drug-discovery-rubrics': () => import('./drug-discovery-rubrics').then(({ drugDiscoveryRubricsModule }) => drugDiscoveryRubricsModule),
  'open-problems-hitl-auto': () => import('./open-problems-hitl-auto').then(({ openProblemsHitlAutoModule }) => openProblemsHitlAutoModule),
  'open-problems-hitl-math': () => import('./open-problems-hitl-math').then(({ openProblemsHitlMathModule }) => openProblemsHitlMathModule),
  'open-problems-hitl-physics': () => import('./open-problems-hitl-physics').then(({ openProblemsHitlPhysicsModule }) => openProblemsHitlPhysicsModule),
  'slop-rewrite': () => import('./slop-rewrite').then(({ slopRewriteModule }) => slopRewriteModule),
  'synthetic-rl-env': () => import('./synthetic-rl-env').then(({ syntheticRlEnvModule }) => syntheticRlEnvModule),
  'workflow-map': () => import('./workflow-map').then(({ workflowMapModule }) => workflowMapModule),
  legacy: () => import('./legacy').then(({ legacyModule }) => legacyModule),
};

/**
 * Ids with an explicit loader edge, including `legacy`. Exported so tests can
 * compare the map against the generated catalog instead of a hand-kept list —
 * a missing edge silently falls back to the read-only legacy view.
 */
export function listTaskTypeRuntimeIds(): string[] {
  return Object.keys(RUNTIME_LOADERS);
}

export function getTaskTypeRuntimeLoader(taskTypeId: string | null | undefined): TaskTypeRuntimeLoader {
  if (taskTypeId && RUNTIME_LOADERS[taskTypeId]) return RUNTIME_LOADERS[taskTypeId];
  return RUNTIME_LOADERS.legacy;
}

export function loadTaskTypeRuntime(taskTypeId: string | null | undefined): Promise<TaskTypeModule<any, any>> {
  return getTaskTypeRuntimeLoader(taskTypeId)();
}
